import { useEffect, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { useLocation, useNavigate, Outlet, useMatch } from 'react-router-dom';
import {
  IconLoader,
  DateInputRange,
  ButtonPseudo,
  IconRefresh,
  ButtonSize,
  IconDownload,
  PermissionEnum,
  PermissionDenied,
  ToggleInput,
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  Checkbox,
  ResponseError,
  getResponseErrors,
  AccountingJob,
  AccountingJobProps,
  Tabs,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { changeVisibility } from './store/slice';
import {
  useExportSafeguardingReportMutation,
  useExportSafeguardingReportWithAccountsMutation,
  useLazyGetSafeguardingCryptoReportQuery,
  useLazyGetSafeguardingReportQuery,
  useExportSafeguardingCryptoReportMutation,
  useExportSafeguardingCryptoReportWithAccountsMutation,
} from './store/services';
import {
  useLazyGetAccountingJobsInProgressQuery,
  useLazyGetAccountingJobsInProgressStatesQuery,
} from '../../store/dictionaries/service';
import { AccountType } from '../types';
import { setNotificationVisible } from '../../../../../store/slice';
import { clearAccountingJobs } from '../../store/dictionaries/slice';
import PrecisionSelector from '../../components/partial/reports/precision-selector';
import { Safeguarding as SafeguardingProps } from './types';

import ROUTES from '../../routes';

import cn from './safeguarding.module.css';

let timeout: NodeJS.Timeout;

const SafeguardingHOC = () => {
  const [dates, setDates] = useState<Date[]>([]);
  const [downloadReportVisible, setDownloadReportVisible] = useState(false);
  const [downloadReportDates, setDownloadReportDates] = useState<Date[]>([]);
  const [type, setType] = useState('without');
  const [
    downloadIncludeSegregatedAccounts,
    setDownloadIncludeSegregatedAccounts,
  ] = useState(false);
  const [errorList, setErrorList] = useState('');

  const [excludeRevaluation, setExcludeRevaluation] = useState<boolean>(false);
  const [includeSegregatedAccounts, setIncludeSegregatedAccounts] =
    useState<boolean>(false);
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();
  const [precision, setPrecision] = useState('2');
  const [cryptoEnabled, setCryptoEnabled] = useState(false);

  const match = useMatch(ROUTES.REPORTS_SAFEGUARDING);
  const cryptoMatch = useMatch(ROUTES.REPORTS_SAFEGUARDING_CRYPTO);

  const {
    safeguarding: {
      safeguarding,
      safeguardingCrypto,
      itemsVisible,
      formationTime,
      formationTimeCrypto,
    },
    reportsDictionaries: { activePermissions, accountingJobs },
  } = useAppSelector((state) => state);
  const { currencies } = useAppSelector((state) => state.root);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { search } = useLocation();

  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetAccountingJobsInProgressQuery();
  const [getAccountingJobsStates] =
    useLazyGetAccountingJobsInProgressStatesQuery();

  const [
    exportSafeguardingReport,
    { isLoading: exportInProgress, isSuccess: exportIsSuccess },
  ] = useExportSafeguardingReportMutation();

  const [
    exportSafeguardingCryptoReport,
    { isLoading: cryptoExportInProgress, isSuccess: cryptoExportIsSuccess },
  ] = useExportSafeguardingCryptoReportMutation();

  const [
    exportSafeguardingReportWithAccounts,
    {
      isLoading: exportWithAccountsInProgress,
      isSuccess: exportWithAccountsIsSuccess,
    },
  ] = useExportSafeguardingReportWithAccountsMutation();
  const [
    exportSafeguardingCryptoReportWithAccounts,
    {
      isLoading: cryptoExportWithAccountsInProgress,
      isSuccess: cryptoExportWithAccountsIsSuccess,
    },
  ] = useExportSafeguardingCryptoReportWithAccountsMutation();

  const [getSafeguardingReport, { isLoading, isFetching }] =
    useLazyGetSafeguardingReportQuery();
  const [
    getSafeguardingCryptoReport,
    { isLoading: cryptoIsLoading, isFetching: cryptoIsFetching },
  ] = useLazyGetSafeguardingCryptoReportQuery();

  const getSafeguardingReportAction = useCallback(
    async (searchValue: string) => {
      const response = (await getSafeguardingReport(
        searchValue,
      )) as unknown as {
        data?: SafeguardingProps;
        error?: ResponseError;
        statusCode: number;
      };

      if (response?.error) {
        getResponseErrors({
          error: response.error,
          setErrorList,
        });
      }
    },
    [],
  );

  const getSafeguardingCryptoReportAction = useCallback(
    async (searchValue: string) => {
      const response = (await getSafeguardingCryptoReport(
        searchValue,
      )) as unknown as {
        data?: SafeguardingProps;
        error?: ResponseError;
        statusCode: number;
      };

      if (response?.error) {
        getResponseErrors({
          error: response.error,
          setErrorList,
        });
      }
    },
    [],
  );

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const onVisibleChange = (accountType: AccountType) => {
    let nextAccountsType = ([] as AccountType[]).concat(itemsVisible);

    if (nextAccountsType.includes(accountType)) {
      nextAccountsType = nextAccountsType.filter(
        (item) => item !== accountType,
      );

      if (
        accountType === AccountType.GROUP &&
        nextAccountsType.includes(AccountType.PRIMARY_ACCOUNT)
      ) {
        nextAccountsType = [];
      }
    } else {
      nextAccountsType.push(accountType);

      if (
        accountType === AccountType.PRIMARY_ACCOUNT &&
        !nextAccountsType.includes(AccountType.GROUP)
      ) {
        nextAccountsType.push(AccountType.GROUP);
      }
    }

    dispatch(changeVisibility(nextAccountsType));
  };

  const onExportClick = async () => {
    const body: {
      createdAtFrom?: string;
      createdAtTo?: string;
      roundDecimals?: string;
      includeSegregatedAccounts?: boolean;
    } = {};

    if (downloadReportDates && downloadReportDates.length) {
      body.createdAtFrom = new Date(downloadReportDates[0]).toISOString();

      if (downloadReportDates.length > 1) {
        body.createdAtTo = new Date(downloadReportDates[1]).toISOString();
      }
    }

    if (precision !== '2') {
      body.roundDecimals = precision;
    }

    if (downloadIncludeSegregatedAccounts) {
      body.includeSegregatedAccounts = downloadIncludeSegregatedAccounts;
    }

    if (type === 'without') {
      if (cryptoMatch) {
        await exportSafeguardingCryptoReport(body);
      } else {
        await exportSafeguardingReport(body);
      }
    } else if (type === 'with') {
      if (cryptoMatch) {
        await exportSafeguardingCryptoReportWithAccounts(body);
      } else {
        await exportSafeguardingReportWithAccounts(body);
      }
    }
  };

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (currencies) {
      if (currencies?.filter((item) => !item.code)?.length) {
        setCryptoEnabled(true);
      }
    }
  }, [currencies]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );

      onGetStates(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearAccountingJobs());

      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    if (
      match &&
      activePermissions?.includes(PermissionEnum.REPORTS_SAFEGUARDING_LIST)
    ) {
      navigate(ROUTES.REPORTS_SAFEGUARDING_FIAT);
    }
  }, [match, activePermissions]);

  useEffect(() => {
    if (
      exportIsSuccess ||
      exportWithAccountsIsSuccess ||
      cryptoExportIsSuccess ||
      cryptoExportWithAccountsIsSuccess
    ) {
      setDownloadReportVisible(false);
      setDownloadReportDates(dates);
      setType('without');

      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'safeguarding-report',
        }),
      );
    }
  }, [
    exportIsSuccess,
    exportWithAccountsIsSuccess,
    cryptoExportIsSuccess,
    cryptoExportWithAccountsIsSuccess,
  ]);

  useEffect(() => {
    let title = 'Safeguarding report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let globalContent = (
    <div className={cn.safeguardingGlobalLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      !activePermissions.includes(PermissionEnum.REPORTS_SAFEGUARDING_LIST) ||
      !activePermissions.includes(PermissionEnum.SECTION_REPORTS)
    ) {
      globalContent = <PermissionDenied />;
    } else if (
      activePermissions.includes(PermissionEnum.REPORTS_SAFEGUARDING_LIST)
    ) {
      globalContent = (
        <div className={cn.safeguarding}>
          <div className={cn.safeguardingTop}>
            <div className={cn.safeguardingTitle}>Safeguarding reports</div>
            <div className={cn.safeguardingTopControls}>
              <div className={cn.safeguardingTopControlsLeft}>
                {cryptoEnabled && (
                  <Tabs
                    wrapperClassName={cn.balanceTopTabs}
                    tabs={[
                      {
                        name: 'Fiat',
                        path: `${ROUTES.REPORTS_SAFEGUARDING_FIAT}${search || ''}`,
                      },
                      {
                        name: 'Crypto',
                        path: `${ROUTES.REPORTS_SAFEGUARDING_CRYPTO}${search || ''}`,
                      },
                    ]}
                    enableRouting
                  />
                )}

                <DateInputRange
                  placeholder="All time"
                  className={cn.safeguardingDateInput}
                  values={
                    dates?.length > 0
                      ? dates.map((item) => item?.toISOString())
                      : []
                  }
                  onReset={() => {
                    let link = '';

                    if (excludeRevaluation) {
                      link = `?excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);

                    setDates([]);
                    setDownloadReportDates([]);
                  }}
                  onChangeRange={(dateValues) => {
                    setDates(dateValues);
                    setDownloadReportDates(dateValues);

                    let link = `?transferDateFrom=${format(
                      new Date(dateValues[0]),
                      'yyyy-MM-dd',
                    )}`;

                    if (dateValues.length > 1) {
                      link = `${link}&transferDateTo=${format(
                        new Date(dateValues[1]),
                        'yyyy-MM-dd',
                      )}`;
                    }

                    if (excludeRevaluation) {
                      link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);
                  }}
                />
              </div>
              <div className={cn.safeguardingTopControlsRight}>
                <ButtonPseudo
                  text="Refresh"
                  icon={
                    <IconRefresh
                      className={
                        (isFetching && safeguarding) ||
                        (cryptoIsFetching && safeguardingCrypto)
                          ? cn.refreshing
                          : ''
                      }
                    />
                  }
                  className={`button-white ${cn.safeguardingRefreshButton}`}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    if (cryptoMatch) {
                      getSafeguardingCryptoReportAction(search);
                    } else {
                      getSafeguardingReportAction(search);
                    }
                    setErrorList('');
                  }}
                  disabled={isLoading || isFetching}
                />
                {activePermissions?.includes(
                  PermissionEnum.REPORTS_SAFEGUARDING_EXPORT_ACTION,
                ) && (
                  <ButtonPseudo
                    text="Download Report"
                    icon={<IconDownload />}
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={() => setDownloadReportVisible(true)}
                    disabled={Boolean(errorList)}
                  />
                )}
              </div>
            </div>
            <div className={cn.safeguardingSearchInfo}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {cryptoMatch
                ? formationTimeCrypto?.end
                  ? `Formed in ${formationTimeCrypto?.duration} sec: ${new Date(
                      formationTimeCrypto?.start || '',
                    ).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                      day: 'numeric',
                    })} from ${new Date(formationTimeCrypto?.start || '')
                      .toLocaleString()
                      .slice(-8)} to ${new Date(formationTimeCrypto?.end)
                      .toLocaleString()
                      .slice(-8)}`
                  : 'Forming...'
                : formationTime?.end
                  ? `Formed in ${formationTime?.duration} sec: ${new Date(
                      formationTime?.start || '',
                    ).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                      day: 'numeric',
                    })} from ${new Date(formationTime?.start || '')
                      .toLocaleString()
                      .slice(-8)} to ${new Date(formationTime?.end)
                      .toLocaleString()
                      .slice(-8)}`
                  : 'Forming...'}
            </div>
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
            ) && (
              <AccountingJob
                accountingJobsIsLoading={
                  accountingJobsIsLoading || accountingJobsIsFetching
                }
                accountingJob={currentAccountingJob}
                accountingJobs={accountingJobs}
                jobsLength={accountingJobs?.length}
                query="?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW"
              />
            )}
          </div>
          <div className={cn.safeguardingSheetVisibilityControls}>
            <div className={cn.safeguardingSheetVisibilityControlsLeft}>
              <ToggleInput
                name="groups"
                title="Groups"
                checked={itemsVisible.includes(AccountType.GROUP)}
                onChange={() => onVisibleChange(AccountType.GROUP)}
                className={cn.safeguardingSheetVisibilityToggle}
                visualClassName={cn.safeguardingSheetVisibilityToggleVisual}
              />
              <ToggleInput
                name="primary"
                title="Primary Accounts"
                checked={itemsVisible.includes(AccountType.PRIMARY_ACCOUNT)}
                onChange={() => onVisibleChange(AccountType.PRIMARY_ACCOUNT)}
                className={cn.safeguardingSheetVisibilityToggle}
                visualClassName={cn.safeguardingSheetVisibilityToggleVisual}
              />
              <ToggleInput
                name="includeSegregatedAccounts"
                title="Include Segregated Accounts"
                checked={includeSegregatedAccounts}
                onChange={(e) => {
                  setIncludeSegregatedAccounts(e.target.checked);

                  let link = '';

                  if (e.target.checked) {
                    link = `${link}includeSegregatedAccounts=${e.target.checked}`;
                  }

                  if (dates && dates?.length) {
                    link = `${link}${
                      e.target.checked ? '&' : ''
                    }transferDateFrom=${format(
                      new Date(dates[0]),
                      'yyyy-MM-dd',
                    )}`;

                    if (dates?.length > 1) {
                      link = `${link}&transferDateTo=${format(
                        new Date(dates[1]),
                        'yyyy-MM-dd',
                      )}`;
                    }
                  }

                  if (excludeRevaluation) {
                    link = `?excludeRevaluation=${excludeRevaluation}`;
                  }

                  if (precision !== '2') {
                    link = `${link}&roundDecimals=${precision}`;
                  }

                  navigate(`?${link.replace('?', '')}`);
                }}
                className={cn.safeguardingSheetVisibilityToggle}
                visualClassName={cn.safeguardingSheetVisibilityToggleVisual}
              />
              <ToggleInput
                name="excludeRevaluation"
                title="Exclude Revaluation"
                checked={excludeRevaluation}
                onChange={(e) => {
                  setExcludeRevaluation(e.target.checked);

                  let link = '';

                  if (e.target.checked) {
                    link = `${link}excludeRevaluation=${e.target.checked}`;
                  }

                  if (dates && dates?.length) {
                    link = `${link}${
                      e.target.checked ? '&' : ''
                    }transferDateFrom=${format(
                      new Date(dates[0]),
                      'yyyy-MM-dd',
                    )}`;

                    if (dates?.length > 1) {
                      link = `${link}&transferDateTo=${format(
                        new Date(dates[1]),
                        'yyyy-MM-dd',
                      )}`;
                    }
                  }

                  if (precision !== '2') {
                    link = `${link}&roundDecimals=${precision}`;
                  }

                  navigate(`?${link.replace('?', '')}`);
                }}
                className={cn.safeguardingSheetVisibilityToggle}
                visualClassName={cn.safeguardingSheetVisibilityToggleVisual}
              />
            </div>

            <div className={cn.safeguardingSheetVisibilityControlsRight}>
              <PrecisionSelector
                precision={precision}
                setPrecision={(value) => {
                  setPrecision(value);

                  let link = `?roundDecimals=${value}`;

                  if (dates && dates?.length) {
                    link = `${link}&transferDateFrom=${format(
                      new Date(dates[0]),
                      'yyyy-MM-dd',
                    )}`;

                    if (dates?.length > 1) {
                      link = `${link}&transferDateTo=${format(
                        new Date(dates[1]),
                        'yyyy-MM-dd',
                      )}`;
                    }
                  }

                  if (excludeRevaluation) {
                    link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                  }

                  navigate(`?${link.replace('?', '')}`);
                }}
              />
            </div>
          </div>
          <Outlet
            context={{
              setExcludeRevaluation,
              setIncludeSegregatedAccounts,
              isLoading: isLoading || cryptoIsLoading,
              isFetching: isFetching || cryptoIsFetching,
              setPrecision,
              setDates,
              setDownloadReportDates,
              getSafeguardingReportAction,
              errorList,
              getSafeguardingCryptoReportAction,
            }}
          />

          <SidePanel
            title="Download report"
            isOpen={downloadReportVisible}
            onClose={() => {
              setDownloadReportVisible(false);
              setDownloadReportDates(dates);
              setType('without');
            }}
            sidepanelExpandedKey="download-report-panel"
            type={SidePanelType.AML}
            footer={
              <div className={cn.statementPanelControls}>
                <ButtonPrimary
                  text={
                    exportInProgress ||
                    exportWithAccountsInProgress ||
                    cryptoExportInProgress ||
                    cryptoExportWithAccountsInProgress
                      ? 'Loading...'
                      : 'Download'
                  }
                  icon={<IconDownload />}
                  disabled={
                    exportInProgress ||
                    exportWithAccountsInProgress ||
                    cryptoExportInProgress ||
                    cryptoExportWithAccountsInProgress
                  }
                  onClick={onExportClick}
                  bold
                />
                <ButtonSecondary
                  className="button-white"
                  text="Close"
                  onClick={() => {
                    setDownloadReportDates(dates);
                    setDownloadReportVisible(false);
                    setType('without');
                  }}
                  bold
                />
              </div>
            }
            closeOnClickOutside
          >
            <>
              <div className={cn.exportTypeTitle}>Export type</div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="without"
                  title="Without accounts"
                  checked={type === 'without'}
                  onChange={() => setType('without')}
                />
              </div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="with"
                  title="With accounts"
                  checked={type === 'with'}
                  onChange={() => setType('with')}
                />
              </div>
              <div className={cn.toggleWrapper}>
                <ToggleInput
                  name="downloadIncludeSegregatedAccounts"
                  title="Include Segregated Accounts"
                  checked={downloadIncludeSegregatedAccounts}
                  onChange={(e) => {
                    setDownloadIncludeSegregatedAccounts(e.target.checked);
                  }}
                  className={cn.safeguardingSheetVisibilityToggle}
                  visualClassName={cn.safeguardingSheetVisibilityToggleVisual}
                />
              </div>
              <div
                className={`${cn.datesWrapper} ${
                  !downloadReportDates.length ? cn.empty : ''
                }`}
              >
                <div className={cn.dateInputTitle}>Report period</div>
                <DateInputRange
                  values={downloadReportDates?.map((item) =>
                    item.toISOString(),
                  )}
                  onChangeRange={(date) => setDownloadReportDates(date)}
                  placeholder="Select date"
                  withoutTwoPickers
                  withoutCustomSelector
                />
              </div>
            </>
          </SidePanel>
        </div>
      );
    }
  }

  return globalContent;
};

export default SafeguardingHOC;
